import { getAuthToken } from './authClient';
import { getJSON, postJSON, NOT_MODIFIED, type NotModified } from './apiClient';
import type {
  KdsAllDayResponse,
  KdsBoardResponse,
  KdsBootstrap,
  KdsLineStatus,
  KdsPriority,
  KdsStreamEvent,
  KdsTicket,
  KdsViewMode,
} from '../types/kds';

// ─────────────────────────────────────────────────────────────────────────────
// KDS transport.
//
// Reads and writes go through the existing apiClient (Bearer token, ETag on
// GETs). The only thing that needs its own machinery is the SSE stream, and
// that is mostly because EventSource has one awkward limitation: it cannot set
// request headers, so the JWT has to travel in the query string. The endpoint
// is built for that (see the comment on /kds/stream in kds.routes.ts).
// ─────────────────────────────────────────────────────────────────────────────

const BASE = '/api/v1/kds';

export async function fetchBootstrap(deviceKey: string): Promise<KdsBootstrap> {
  const result = await getJSON<KdsBootstrap>(
    `${BASE}/bootstrap?deviceKey=${encodeURIComponent(deviceKey)}`,
  );
  if (result === NOT_MODIFIED) throw new Error('unexpected 304 on bootstrap');
  return result;
}

/**
 * The board for one view.
 *
 * `force` bypasses the ETag. Pass it whenever the caller has just discarded the
 * tickets it was holding — see the note on `getJSON`.
 */
export function fetchBoard(
  stationId: number | null,
  mode: KdsViewMode,
  opts: { force?: boolean; date?: string | null } = {},
): Promise<KdsBoardResponse | NotModified> {
  const params = new URLSearchParams({ mode });
  if (stationId != null) params.set('stationId', String(stationId));
  // Omitted on the first load ON PURPOSE: no date means "the tenant's today",
  // decided by the server. The tablet's own clock is not consulted, and the
  // response tells us which day it picked.
  if (opts.date) params.set('date', opts.date);
  return getJSON<KdsBoardResponse>(`${BASE}/board?${params.toString()}`, opts);
}

export function fetchAllDay(
  stationId: number | null,
  opts: { force?: boolean } = {},
): Promise<KdsAllDayResponse | NotModified> {
  const params = new URLSearchParams();
  if (stationId != null) params.set('stationId', String(stationId));
  return getJSON<KdsAllDayResponse>(`${BASE}/allday?${params.toString()}`, opts);
}

export function fetchRecall(
  stationId: number | null,
  opts: { force?: boolean } = {},
): Promise<KdsBoardResponse | NotModified> {
  const params = new URLSearchParams();
  if (stationId != null) params.set('stationId', String(stationId));
  return getJSON<KdsBoardResponse>(`${BASE}/recall?${params.toString()}`, opts);
}

type TicketReply = { ticket: KdsTicket };

export const startTicket = (id: number): Promise<TicketReply> =>
  postJSON<TicketReply>(`${BASE}/tickets/${id}/start`);

export const bumpTicket = (id: number): Promise<TicketReply> =>
  postJSON<TicketReply>(`${BASE}/tickets/${id}/bump`);

/**
 * Serve, optionally naming the driver who took it.
 *
 * `driverId` is omitted from the body when null rather than sent as null, so a
 * plain serve stays byte-identical to what this app has always posted.
 */
export const serveTicket = (id: number, driverId?: number | null): Promise<TicketReply> =>
  postJSON<TicketReply>(
    `${BASE}/tickets/${id}/serve`,
    driverId == null ? undefined : { driverId },
  );

export const recallTicket = (id: number, reason?: string): Promise<TicketReply> =>
  postJSON<TicketReply>(`${BASE}/tickets/${id}/recall`, { reason: reason ?? null });

export const setTicketPriority = (id: number, priority: KdsPriority): Promise<TicketReply> =>
  postJSON<TicketReply>(`${BASE}/tickets/${id}/priority`, { priority });

export const setTicketHold = (id: number, held: boolean): Promise<TicketReply> =>
  postJSON<TicketReply>(`${BASE}/tickets/${id}/hold`, { held });

export const setLineStatus = (
  lineId: number,
  status: KdsLineStatus,
  qtyReady?: number | null,
): Promise<TicketReply> =>
  postJSON<TicketReply>(`${BASE}/lines/${lineId}/status`, {
    status,
    qtyReady: qtyReady ?? null,
  });

export const fireCourse = (orderId: number, courseNo: number): Promise<{ tickets: KdsTicket[] }> =>
  postJSON<{ tickets: KdsTicket[] }>(`${BASE}/orders/${orderId}/fire-course`, { courseNo });

export interface PrintReply {
  sent: number;
  printers: string[];
  customerReceipt: string;
  partial: boolean;
}

/** Reprint the customer receipt for the order behind this ticket. */
export const printTicketReceipt = (id: number): Promise<PrintReply> =>
  postJSON<PrintReply>(`${BASE}/tickets/${id}/print`);

/** Re-send the kitchen slip to the station printers. */
export const printTicketKitchen = (id: number): Promise<PrintReply> =>
  postJSON<PrintReply>(`${BASE}/tickets/${id}/kitchen-print`);

/**
 * item id → category ids, for the board's category filter. Read once per
 * screen: the catalogue is stable and this is not on the hot path.
 */
export const fetchItemCategories = (): Promise<{ items: Array<[number, number[]]> } | NotModified> =>
  getJSON<{ items: Array<[number, number[]]> }>(`${BASE}/item-categories`);

export const registerDevice = (body: {
  deviceKey: string;
  name?: string | null;
  stationId?: number | null;
  viewMode?: KdsViewMode;
}): Promise<{ device: unknown }> => postJSON(`${BASE}/devices`, body);

// ── SSE ─────────────────────────────────────────────────────────────────────

export interface StreamHandle {
  close: () => void;
}

/**
 * Open the push stream.
 *
 * EventSource handles reconnection natively — after a dropped connection it
 * retries with backoff, forever, with no code from us. That is most of the
 * reason this is SSE and not a WebSocket: the reconnect logic we would
 * otherwise have to write is the part that goes wrong at 8pm on a Friday.
 *
 * `onStatus` fires on open and error so the store can show a live/degraded
 * indicator and decide whether the polling fallback needs to take over. Note
 * that an EventSource 'error' does NOT mean dead — it fires on every transient
 * blip while the browser retries — so the store treats it as "degraded", not
 * "closed", and only falls back to polling once pings stop arriving.
 */
export function openStream(
  stationId: number | null,
  mode: KdsViewMode,
  onEvent: (event: KdsStreamEvent) => void,
  onStatus: (status: 'open' | 'error') => void,
): StreamHandle {
  const token = getAuthToken();
  const params = new URLSearchParams({ mode });
  if (stationId != null) params.set('stationId', String(stationId));
  if (token) params.set('token', token);

  const source = new EventSource(`${BASE}/stream?${params.toString()}`);

  source.onopen = () => onStatus('open');
  source.onerror = () => onStatus('error');
  source.onmessage = (message: MessageEvent<string>) => {
    try {
      onEvent(JSON.parse(message.data) as KdsStreamEvent);
    } catch {
      // A malformed frame must not tear down the stream; the next one is a
      // fraction of a second away and the polling fallback backstops us.
    }
  };

  return {
    close: () => {
      source.onopen = null;
      source.onerror = null;
      source.onmessage = null;
      source.close();
    },
  };
}

/**
 * This screen's stable identity, so a tablet that reboots comes back to the
 * station it was showing rather than to a picker. Generated once and kept in
 * localStorage; losing it costs one re-pick, so no ceremony around it.
 */
export function deviceKey(): string {
  const KEY = 'matix_kds_device';
  let value = localStorage.getItem(KEY);
  if (!value) {
    value =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `dev-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem(KEY, value);
  }
  return value;
}
